/**
 * LA PART DE L'ASSOCIATION, ÉVÉNEMENT PAR ÉVÉNEMENT (Comptabilité, 16/10/2026).
 *
 * « Ça dépend de l'asso » : ce que chaque association récupère par billet
 * vendu. Lue avec les Règles de la Comptabilité (`finance.reference.read`),
 * réglée avec leur droit (`finance.settings.manage`, ré-authentification) ;
 * absente = le défaut des Règles.
 *
 * UN SEUL COMPOSANT, DEUX ÉCRANS : Comptabilité › Règles, que le rôle compta
 * ouvre, et Réglages Soirées › Billetteries, à côté de la nuit de chaque
 * événement. Avant la revue du 16/10, seul le second existait — et le rôle
 * `compta_administration`, qui a le droit de régler la part, s'y voyait
 * refuser l'accès (il n'a pas `soiree.read`).
 *
 * Le réglage est un geste ATOMIQUE (bouton explicite, jamais la barre
 * d'enregistrement) : une part change le chiffre des nuits pas encore
 * finalisées, et c'est tracé.
 */

import { useState } from 'react'
import { adminMutate, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { formatMoney, parseMoney } from './money.js'
import './finance-associations.css'

/** Une part réglée (ou non) pour un événement, telle que `GET /finance/associations/shares` la sert. */
export interface PartLue {
  share_cents: number | null
  reason: string | null
  updated_by_name: string | null
  updated_at: string | null
}

/** Ce que les deux écrans lisent des Règles pour dire la part. */
export interface PartsAssociations {
  default_share_cents: number
  revenue_enabled: boolean
  entries_enabled: boolean
  parEvenement: Map<string, PartLue>
}

/** La réponse de `GET /api/v1/finance/associations/shares`, rangée. */
export interface EvenementPart extends PartLue { event_id: string; name: string; night: string }

export function lireParts(j: {
  default_share_cents: number; revenue_enabled: boolean; entries_enabled?: boolean; events: EvenementPart[]
}): PartsAssociations {
  return {
    default_share_cents: j.default_share_cents,
    revenue_enabled: j.revenue_enabled,
    // Un serveur d'avant la revue ne l'envoie pas : allumé, son défaut.
    entries_enabled: j.entries_enabled !== false,
    parEvenement: new Map(j.events.map((e) => [e.event_id, e])),
  }
}

/** « 2 » ou « 2,5 » : la saisie d'un montant, à la française. */
const enSaisie = (c: number): string => String(c / 100).replace('.', ',')

export function PartAssociation({ evenement, parts, peutRegler, onFait }: {
  evenement: { id: string; name: string }
  parts: PartsAssociations
  peutRegler: boolean
  onFait: () => void
}) {
  const toast = useToast()
  const reglee = parts.parEvenement.get(evenement.id) ?? null
  const actuelle = reglee?.share_cents ?? null
  const [ouvert, setOuvert] = useState(false)
  const [saisie, setSaisie] = useState('')
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const cents = parseMoney(saisie)

  // La saisie part de la part EN VIGUEUR à chaque ouverture : figée au
  // montage, elle gardait l'ancienne valeur après « Revenir au défaut ».
  const ouvrir = () => {
    setSaisie(enSaisie(actuelle ?? parts.default_share_cents)); setMotif(''); setErreur(null); setOuvert(true)
  }

  const envoyer = async (valeur: number | null) => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('PUT', `/api/v1/finance/associations/${evenement.id}/share`, {
        share_cents: valeur, reason: motif.trim() === '' ? null : motif.trim(),
      })
      if (r.status !== 200) { setErreur(r.data.message ?? 'Réglage impossible.'); return }
      toast('ok', valeur === null
        ? `« ${evenement.name} » suit de nouveau la part par défaut (${formatMoney(parts.default_share_cents)} par billet).`
        : `« ${evenement.name} » : l’association récupère ${formatMoney(valeur)} par billet vendu.`)
      setOuvert(false); setMotif(''); onFait()
    } finally { setEnvoi(false) }
  }

  return (
    <div className="fba-part">
      <div className="fba-part__ligne">
        <span className="fba-part__texte">
          Part de l’association : <strong>{formatMoney(actuelle ?? parts.default_share_cents)}</strong> par billet vendu
          {actuelle === null ? ' (défaut de la Comptabilité)' : ' (réglée pour cet événement)'}
          {reglee?.reason ? ` — « ${reglee.reason} »` : ''}
          {!parts.revenue_enabled ? <span className="fba__sous"> · sans effet tant que la billetterie des associations reste hors du chiffre</span> : null}
        </span>
        {peutRegler && !ouvert ? (
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={ouvrir}>Régler la part</button>
        ) : null}
      </div>
      {ouvert ? (
        <div className="fba-part__form">
          <Field label="Part de l’association par billet vendu" helper="Jamais plus que le prix du billet. Les nuits déjà finalisées ne changent pas." error={erreur}>
            <TextInput value={saisie} onChange={setSaisie} placeholder="2" ariaLabel={`Part de l’association pour ${evenement.name}`} />
          </Field>
          <Field label="Pourquoi (facultatif)" helper="Gardé avec la part et dans le journal : « accord BDE pharma 2026 » se relit six mois plus tard.">
            <TextInput value={motif} onChange={setMotif} maxLength={300} placeholder="Accord passé avec le BDE" ariaLabel="Motif de la part" />
          </Field>
          <span className="fba-part__actions">
            <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || cents === null || cents < 0 || cents > 100_000}
              onClick={() => { void envoyer(cents) }}>
              {envoi ? 'Enregistrement en cours' : 'Enregistrer la part'}
            </button>
            {actuelle !== null ? (
              <button type="button" className="lc-btn lc-btn--ghost" disabled={envoi} onClick={() => { void envoyer(null) }}>Revenir au défaut</button>
            ) : null}
            <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { setOuvert(false); setErreur(null) }}>Annuler</button>
          </span>
        </div>
      ) : null}
    </div>
  )
}
